import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"

import { useAuthStore } from "@/modules/helpers/hooks/shared"
import { sendHaptic } from "@/modules/helpers/utils"
import type { PagingSchema, SchemaResponse } from "@/modules/types/common/schema"

type UseInfiniteScrollQueryParams<_TQueryFnData, TItem> = {
  queryKey: string[]
  queryFn: (params: any) => Promise<AxiosResponse<SchemaResponse<PagingSchema<TItem>>>>
  params?: any
  enabled?: boolean
}

export const getNextPageToken = (res: any): string | undefined => {
  return res?.data?.data?.nextPageToken
}
export const useInfiniteScrollQuery = <TQueryFnData, TItem>({
  queryKey,
  queryFn,
  params,
  enabled = true
}: UseInfiniteScrollQueryParams<TQueryFnData, TItem>) => {
  const { accessToken } = useAuthStore()
  const queryClient = useQueryClient()

  const hook = useInfiniteQuery({
    queryKey: [...queryKey, accessToken],
    enabled,
    queryFn: ({ pageParam: nextPageToken = "" }) =>
      queryFn({
        ...params,
        ...(nextPageToken ? { nextPageToken } : {})
      }),
    initialPageParam: "",
    getNextPageParam: (lastPage: any) => {
      const nextPageToken = lastPage?.data?.data?.nextPageToken

      // If API provides a nextPageToken, it means there's more data to fetch
      return nextPageToken ? nextPageToken : undefined
    },
    select: (data) => {
      const list = data.pages.reduce((acc: any, cur) => {
        return [...acc, ...(cur?.data?.data?.list || [])]
      }, [] as TItem[]) as TItem[]

      const lastPage = data.pages[data.pages.length - 1]

      return {
        list,
        total: data.pages[0]?.data?.data?.total,
        nextPageToken: lastPage?.data?.data?.nextPageToken,
        hasNext: lastPage?.data?.data?.hasNext
      }
    }
  })
  const forceRefetch = async () => {
    queryClient.removeQueries({ queryKey: [...queryKey, accessToken] })
    hook.refetch()
    sendHaptic()
  }
  return {
    ...hook,
    forceRefetch
  }
}
